import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CollapsibleSection from '../../common/CollapsibleSection';
import { Heading, Text, Typography } from '../../common/Typography';
import Button from '../../common/Button';
import DropdownMenu from '../../common/DropdownMenu';
import SessionNavigationButton from '../../common/SessionNavigationButton';
import ClubLogo from '../../clubs/ClubLogo';
import CancelDeleteModal from '../CancelDeleteModal';
import EditSessionModal from '../EditSessionModal';
import { formatSessionActivityMetaLine, getSessionStatusLabel } from './sessionHubUtils';
import BlockBookingBadge from '../BlockBookingBadge';

const formatSessionMetaLine = (session, { showActivityStats }) =>
  formatSessionActivityMetaLine(session, { showActivityStats });

const scrollToElement = (elementId) => {
  requestAnimationFrame(() => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};

const TYPE_META = {
  live: {
    badge: 'Live',
    badgeClass: 'bg-success-light text-success-dark border-success/30',
    showStatusLabel: false,
  },
  startable: {
    badge: null,
    badgeClass: '',
    showStatusLabel: true,
  },
  'wrap-up': {
    badge: 'Not archived',
    badgeClass: 'bg-warning-light text-warning-dark border-warning/30',
    showStatusLabel: true,
  },
  delete: {
    badge: 'Remove',
    badgeClass: 'bg-danger-light text-danger-dark border-danger/30',
    showStatusLabel: true,
  },
};

function WorkGroup({ title, description, children, className = '' }) {
  return (
    <section className={className}>
      <div className="mb-3">
        <Heading level={3} className="text-sm font-semibold text-text-primary">
          {title}
        </Heading>
        {description ? (
          <Text className="text-xs text-text-secondary mt-0.5">{description}</Text>
        ) : null}
      </div>
      <ul className="space-y-3">{children}</ul>
    </section>
  );
}

function getStartableMoreItems({
  session,
  onEditSession,
  onUpdateSessionStatus,
  onManageSignups,
  onRequestCancelDelete,
}) {
  const items = [];
  if (onEditSession) {
    items.push({ label: 'Edit Session', icon: '✏️', onClick: () => onEditSession(session) });
  }
  if (onUpdateSessionStatus) {
    if (session.session_status === 'upcoming') {
      items.push({
        label: 'Make Draft',
        icon: '📝',
        onClick: () => onUpdateSessionStatus(session.id, 'draft'),
      });
    } else if (session.session_status === 'draft') {
      items.push({
        label: 'Make Live',
        icon: '✅',
        onClick: () => onUpdateSessionStatus(session.id, 'upcoming'),
      });
    }
  }
  if (onManageSignups) {
    items.push({
      label: 'View Signups',
      icon: '👥',
      onClick: () => onManageSignups(session.id),
    });
  }
  if (onRequestCancelDelete) {
    items.push(
      { divider: true },
      {
        label: 'Cancel Session',
        icon: '❌',
        onClick: () => onRequestCancelDelete(session),
        variant: 'danger',
      }
    );
  }
  return items;
}

function WorkItemRow({
  type,
  session,
  label,
  clubId,
  clubLogoUrl,
  clubName,
  starterPlayerId,
  onStartSession,
  onWrapUp,
  onDeleteSession,
  onEditSession,
  onUpdateSessionStatus,
  onManageSignups,
  onRequestCancelDelete,
  rowClassName = '',
}) {
  const meta = TYPE_META[type];
  const moreItems =
    type === 'startable'
      ? getStartableMoreItems({
          session,
          onEditSession,
          onUpdateSessionStatus,
          onManageSignups,
          onRequestCancelDelete,
        })
      : [];
  const showActivityStats =
    type === 'live' ||
    type === 'wrap-up' ||
    type === 'delete' ||
    session.session_status === 'ongoing' ||
    session.session_status === 'completed';

  return (
    <li
      className={`flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between ${rowClassName}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <ClubLogo
          logoImageUrl={clubLogoUrl}
          clubName={clubName}
          size="sm"
          className="border border-border-medium shadow-brand-sm mt-0.5"
        />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {type === 'live' ? (
              <span
                className="inline-flex h-2 w-2 shrink-0 rounded-full bg-success animate-pulse"
                aria-hidden
              />
            ) : null}
            <Typography className="font-semibold text-text-primary truncate">{label}</Typography>
            {meta.badge ? (
              <span
                className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium border ${meta.badgeClass}`}
              >
                {meta.badge}
              </span>
            ) : null}
            {meta.showStatusLabel ? (
              <span className="text-xs text-text-muted">{getSessionStatusLabel(session.session_status)}</span>
            ) : null}
            <BlockBookingBadge session={session} />
          </div>
          <Text className="text-xs text-text-secondary mt-1">
            {formatSessionMetaLine(session, { showActivityStats })}
          </Text>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {type === 'live' && (
          <SessionNavigationButton
            session={session}
            clubId={clubId}
            action="enter"
            variant="primary"
            size="small"
            className="w-full sm:w-auto"
          >
            Enter session
          </SessionNavigationButton>
        )}
        {type === 'startable' && (
          <SessionNavigationButton
            session={session}
            clubId={clubId}
            starterPlayerId={starterPlayerId}
            action="start"
            variant="primary"
            size="small"
            onStartSession={onStartSession}
            className="flex-1 sm:flex-none sm:w-auto"
          />
        )}
        {type === 'wrap-up' && (
          <Button
            variant="outline"
            size="small"
            onClick={() => onWrapUp(session.id)}
            className="w-full sm:w-auto"
          >
            Review & archive
          </Button>
        )}
        {type === 'delete' && (
          <Button
            variant="outline"
            size="small"
            onClick={() => onDeleteSession?.(session)}
            className="w-full justify-center border-danger text-danger hover:bg-danger-light sm:w-auto"
          >
            Delete session
          </Button>
        )}
        {type === 'startable' && moreItems.length > 0 ? (
          <DropdownMenu
            usePortal
            trigger={
              <Button variant="outline" size="small" className="flex-1 sm:flex-none" aria-label="More session actions">
                More
              </Button>
            }
            items={moreItems}
            align="right"
          />
        ) : null}
      </div>
    </li>
  );
}

const SessionsTodayWork = ({
  items,
  includeTypes = null,
  overflowWrapUpCount = 0,
  clubId,
  clubLogoUrl,
  clubName,
  selectedClub,
  currentUser,
  starterPlayerId,
  onStartSession,
  onDeleteSession,
  onEditSession,
  onUpdateSessionStatus,
  onCancelSession,
  onManageSignups,
  onViewOverflowArchive,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
  sectionTitle = 'To do list',
  startableTitle = 'Ready to start',
  startableDescription = 'Planned for today — start when players arrive',
}) => {
  const navigate = useNavigate();
  const [sessionToEdit, setSessionToEdit] = useState(null);
  const [sessionToCancelDelete, setSessionToCancelDelete] = useState(null);

  const visibleItems = useMemo(() => {
    if (!includeTypes) return items || [];
    return (items || []).filter((item) => includeTypes.includes(item.type));
  }, [items, includeTypes]);

  const { liveItems, startableItems, adminFollowUpItems } = useMemo(() => {
    const live = [];
    const startable = [];
    const adminFollowUp = [];
    visibleItems.forEach((item) => {
      if (item.type === 'live') live.push(item);
      else if (item.type === 'startable') startable.push(item);
      else if (item.type === 'delete' || item.type === 'wrap-up') adminFollowUp.push(item);
    });
    return {
      liveItems: live,
      startableItems: startable,
      adminFollowUpItems: adminFollowUp,
    };
  }, [visibleItems]);

  if (!visibleItems.length) return null;

  const handleWrapUp = (sessionId) => {
    navigate(`/club/${clubId}/session-wrapup/${sessionId}`);
  };

  const hasRunNow = liveItems.length > 0 || startableItems.length > 0;
  const hasAdminFollowUp = adminFollowUpItems.length > 0;

  return (
    <div id="sessions-today-work">
      <CollapsibleSection
        title={sectionTitle}
        iconType="today-work"
        defaultExpanded={defaultExpanded}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        className="border-l-4 border-l-primary"
      >
      {liveItems.length > 0 && (
        <WorkGroup
          title="Live now"
          description="Courts are running — jump back in"
          className={hasAdminFollowUp || startableItems.length ? 'mb-6' : ''}
        >
          {liveItems.map((item) => (
            <WorkItemRow
              key={`live-${item.session.id}`}
              {...item}
              clubId={clubId}
              clubLogoUrl={clubLogoUrl}
              clubName={clubName}
              starterPlayerId={starterPlayerId}
              onStartSession={onStartSession}
              onWrapUp={handleWrapUp}
              rowClassName="border-success/40 bg-success-light/40 border-l-4 border-l-success shadow-brand-sm"
            />
          ))}
        </WorkGroup>
      )}

      {startableItems.length > 0 && (
        <WorkGroup
          title={startableTitle}
          description={startableDescription}
          className={hasAdminFollowUp ? 'mb-6' : liveItems.length ? 'mb-6' : ''}
        >
          {startableItems.map((item) => (
            <WorkItemRow
              key={`startable-${item.session.id}`}
              {...item}
              clubId={clubId}
              clubLogoUrl={clubLogoUrl}
              clubName={clubName}
              starterPlayerId={starterPlayerId}
              onStartSession={onStartSession}
              onWrapUp={handleWrapUp}
              onEditSession={onEditSession ? setSessionToEdit : undefined}
              onUpdateSessionStatus={onUpdateSessionStatus}
              onManageSignups={onManageSignups}
              onRequestCancelDelete={
                onCancelSession || onDeleteSession ? setSessionToCancelDelete : undefined
              }
              rowClassName="border-border-medium bg-surface-subtle border-l-4 border-l-info"
            />
          ))}
        </WorkGroup>
      )}

      {adminFollowUpItems.length > 0 && (
        <WorkGroup
          title="Admin follow-up"
          description="Most recent first — delete empty sessions, wrap up payments, then archive"
          className={hasRunNow ? 'mt-6' : ''}
        >
          {adminFollowUpItems.map((item) => (
            <WorkItemRow
              key={`${item.type}-${item.session.id}`}
              {...item}
              clubId={clubId}
              clubLogoUrl={clubLogoUrl}
              clubName={clubName}
              starterPlayerId={starterPlayerId}
              onStartSession={onStartSession}
              onWrapUp={handleWrapUp}
              onDeleteSession={onDeleteSession}
              rowClassName="border-border-medium bg-surface-subtle"
            />
          ))}
        </WorkGroup>
      )}

      {overflowWrapUpCount > 0 && (
        <div className="mt-4 pt-4 border-t border-border-medium space-y-2">
          <Text className="text-xs text-text-secondary">
            Showing the 5 most recent. {overflowWrapUpCount} more completed session
            {overflowWrapUpCount === 1 ? '' : 's'} still need review or archive.
          </Text>
          <Button
            variant="outline"
            size="small"
            onClick={() => {
              onViewOverflowArchive?.();
              scrollToElement('sessions-past-section');
            }}
            className="w-full sm:w-auto"
          >
            Open Past sessions ({overflowWrapUpCount} more)
          </Button>
        </div>
      )}
    </CollapsibleSection>

      {sessionToEdit ? (
        <EditSessionModal
          isOpen={true}
          onClose={() => setSessionToEdit(null)}
          session={sessionToEdit}
          selectedClub={selectedClub || sessionToEdit.clubs}
          currentUser={currentUser}
          onSessionUpdated={async (sessionId, formData) => {
            await onEditSession?.(sessionId, formData);
            setSessionToEdit(null);
          }}
        />
      ) : null}

      {sessionToCancelDelete ? (
        <CancelDeleteModal
          isOpen={true}
          onClose={() => setSessionToCancelDelete(null)}
          onCancel={() => {
            onCancelSession?.(sessionToCancelDelete.id);
            setSessionToCancelDelete(null);
          }}
          onDelete={() => {
            onDeleteSession?.(sessionToCancelDelete);
            setSessionToCancelDelete(null);
          }}
          sessionName={sessionToCancelDelete.name || 'Session'}
        />
      ) : null}
    </div>
  );
};

export default SessionsTodayWork;
